// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadAccountsIfNeeded from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/LoadAccountsIfNeeded";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    loadAccountsIfNeeded,
};

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCurrentCartState(state),
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    // XNG-Changes
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url || "/Cart",
});

type Props = ResolveThunks<typeof mapDispatchToProps> & PageProps & ReturnType<typeof mapStateToProps>;

export interface RfqRequestQuotePageStyles {
    loadingOverlay?: LoadingOverlayProps;
    // XNG-Changes
    backToCart?: LinkPresentationProps;
}

export const rfqRequestQuotePageStyles: RfqRequestQuotePageStyles = {
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = rfqRequestQuotePageStyles;

const RfqRequestQuotePage = ({ id, cartState, isUpdatingCartLine, loadAccountsIfNeeded, cartUrl }: Props) => {
    useEffect(() => {
        loadAccountsIfNeeded();
    }, [cartState]);

    // XNG-Changes: adding return to cart link
    return (
        <Page>
            <LoadingOverlay {...styles.loadingOverlay} loading={cartState.isLoading || isUpdatingCartLine}>
                <Link {...styles.backToCart} href={cartUrl}>
                    {translate("Return to Cart")}
                </Link>
                <Zone contentId={id} zoneName="Content" />
            </LoadingOverlay>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqRequestQuotePage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        fieldDefinitions: [],
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "RfqRequestQuotePage" instead of this constant.
 */
export const RfqRequestQuotePageContext = "RfqRequestQuotePage";
